import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { tokens, Badge, Text, Tooltip } from '@hotjar/hotsauce';
import type { BillingCycle } from '@hotjar/account';
import { css } from '@emotion/react';

const S = {
  OuterContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x3};
  `,
  Heading: styled.h3`
    ${tokens.text('subtitle3')};
    font-weight: normal;
  `,
  Container: styled.div<{ highlighted: boolean }>`
    display: flex;
    gap: ${tokens.spacer.x1};
    ${({ highlighted }) =>
      highlighted &&
      css`
        color: ${tokens.color.text_default};
        background: ${tokens.color.bg_active};
        padding: ${tokens.spacer.x2};
      `};
  `,
  DiscountsContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x2};
  `,
  Price: styled.span`
    ${tokens.text('subtitle2')};
    font-weight: normal;
  `,
  DiscountBadge: styled(Badge)<{ highlighted: boolean }>`
    background-color: ${(props) =>
      props.highlighted ? tokens.color.white : tokens.color.ui_active};
    color: ${(props) => (!props.highlighted ? tokens.color.white : tokens.color.ui_active)};
  `,
};

interface Props {
  billingCycle: BillingCycle;
  planDowngradePeriod: boolean;
  total: string;
  agencyDiscountPercentage?: string | number;
  legacyDiscount: string | undefined;
  highlighted: boolean;
  bundleDiscount?: boolean;
}

export function SitePriceMultiSku(props: Props): JSX.Element {
  const {
    billingCycle,
    planDowngradePeriod,
    total,
    agencyDiscountPercentage,
    legacyDiscount,
    highlighted,
    bundleDiscount,
  } = props;
  const { t: tMultiSku } = useTranslationWithPrefix('billing:multi-sku-site-box');
  const isYearly = ['yearly_recurring', 'yearly'].includes(billingCycle);

  return (
    <S.OuterContainer>
      <S.Heading>
        {tMultiSku('cost')} / {isYearly ? tMultiSku('year') : tMultiSku('month')}
        {planDowngradePeriod && <span className="no-transform"> {tMultiSku('next-bill')}</span>}
      </S.Heading>
      <S.Container highlighted={highlighted}>
        {billingCycle === 'yearly' && <span>{tMultiSku('site-price.custom-price')}</span>}
        {billingCycle !== 'yearly' && (
          <>
            <S.Price>{total}</S.Price>
            <S.DiscountsContainer>
              {bundleDiscount && (
                <div>
                  <S.DiscountBadge size="s" kind="custom" highlighted={highlighted}>
                    {tMultiSku('site-price.bundle-discount-badge')}
                  </S.DiscountBadge>
                  <br />
                  <Text textColor="text_light">
                    {tMultiSku('site-price.bundle-discount-info', {
                      discountPercentage: 20,
                    })}
                  </Text>
                </div>
              )}
              {agencyDiscountPercentage !== 0 && agencyDiscountPercentage !== '0' && (
                <div>
                  <S.DiscountBadge size="s" kind="custom" highlighted={highlighted}>
                    {tMultiSku('site-price.agency-discount-badge')}
                  </S.DiscountBadge>
                  <br />
                  <Text textColor="text_light">
                    {tMultiSku('site-price.agency-discount-info', {
                      discountPercentage: agencyDiscountPercentage,
                    })}
                  </Text>
                </div>
              )}
              {legacyDiscount && (
                <div>
                  <Tooltip
                    text={tMultiSku('site-price.legacy-discount-tooltip', {
                      discountAmount: legacyDiscount,
                    })}
                  >
                    <S.DiscountBadge size="s" kind="custom" highlighted={highlighted}>
                      {tMultiSku('site-price.legacy-discount-badge')}
                    </S.DiscountBadge>
                  </Tooltip>
                  <br />
                  <Text textColor="text_light">
                    {tMultiSku('site-price.legacy-discount-info', {
                      discountAmount: legacyDiscount,
                    })}
                  </Text>
                </div>
              )}
            </S.DiscountsContainer>
          </>
        )}
      </S.Container>
    </S.OuterContainer>
  );
}
